import { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { SUB_STATUS_CLS, SUB_STATUS_TR, AUDIT_TR, fmtD, fmtDT } from "@/lib/subscription";
import { ProviderSubDialog } from "@/components/admin/sub/ProviderSubDialog";
import { SubPayments } from "@/components/admin/sub/SubPayments";
import { SubPackages } from "@/components/admin/sub/SubPackages";

const Kpi = ({ label, value, testid, onClick, active }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`rounded-2xl border p-3 text-left transition-colors ${active ? "border-primary bg-primary/10" : "hover:bg-muted/50"}`}>
    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="text-2xl font-extrabold">{value}</div>
  </button>
);

const Providers = ({ ov, filter, onOpen }) => {
  const [q, setQ] = useState("");
  const rows = ov.providers.filter((r) => (filter === "all" || (filter === "soon" ? r.days_left != null && r.days_left <= 7 && r.ends_at : r.status === filter))
    && (!q || `${r.name} ${r.email} ${r.phone} ${r.businesses.join(" ")}`.toLowerCase().includes(q.toLowerCase())));
  return (
    <div className="space-y-2">
      <Input placeholder="Ad, e-posta, telefon veya işletme ara" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" data-testid="sub-search" />
      {!rows.length && <div className="text-sm text-muted-foreground">Kayıt yok.</div>}
      {rows.map((r) => (
        <button type="button" key={r.id} onClick={() => onOpen(r.id)} data-testid={`sub-row-${r.id}`}
          className="w-full text-left rounded-xl border p-3 flex flex-wrap items-center justify-between gap-2 hover:bg-muted/40">
          <div className="min-w-0">
            <div className="font-medium truncate">{r.businesses.join(", ") || r.company_name || "İşletme yok"}</div>
            <div className="text-xs text-muted-foreground truncate">{r.name} · {r.email} · {r.phone || "—"}</div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            {r.trial_review === "pending" && <Badge variant="outline">İnceleme</Badge>}
            {r.ends_at && <span>Bitiş {fmtD(r.ends_at)} ({r.days_left} gün)</span>}
            <Badge className={SUB_STATUS_CLS[r.status]}>{r.status_label}</Badge>
          </div>
        </button>
      ))}
    </div>
  );
};

const TrialReviews = ({ onOpen, onChanged }) => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => { api.get("/admin/subscriptions/trial-reviews").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (id, action, msg) => {
    if (!window.confirm(msg)) return;
    try { await api.post(`/admin/subscriptions/providers/${id}/action`, { action }); toast.success("Karar kaydedildi"); load(); onChanged(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-2" data-testid="sub-trial-reviews">
      <p className="text-xs text-muted-foreground">Ortak e-posta/telefon eşleşmesi nedeniyle deneme verilmeyen işletmeler. Farklı gerçek bir işletmeyse istisna tanıyın; aynı işletmeyse reddi onaylayın (işletme yine paket satın alabilir).</p>
      {rows?.length === 0 && <div className="text-sm text-muted-foreground">İnceleme bekleyen yok.</div>}
      {rows?.map((r) => (
        <div key={r.provider.id} className="rounded-2xl border p-3 text-sm" data-testid={`sub-review-${r.provider.id}`}>
          <div><b>{r.provider.company_name || r.provider.name}</b> · {r.provider.email} · {r.provider.phone} · onay {fmtD(r.approved_at)}</div>
          <div className="text-xs mt-1">Eşleşen hesap(lar): {r.matches.map((m) => `${m.company_name || m.name} (${m.email}, ${m.phone || "—"})`).join("; ") || "—"}</div>
          <div className="flex gap-2 mt-2 flex-wrap">
            <Button size="sm" onClick={() => act(r.provider.id, "grant_trial_exception", "Bu işletmeye istisna olarak ücretsiz deneme verilsin mi?")} data-testid={`sub-review-grant-${r.provider.id}`}>İstisna tanı, deneme ver</Button>
            <Button size="sm" variant="outline" onClick={() => act(r.provider.id, "confirm_trial_denial", "Deneme reddi onaylansın mı?")} data-testid={`sub-review-deny-${r.provider.id}`}>Reddi onayla</Button>
            <Button size="sm" variant="ghost" onClick={() => onOpen(r.provider.id)}>Ayrıntı</Button>
          </div>
        </div>
      ))}
    </div>
  );
};

const AuditLog = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/subscriptions/audit").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  return (
    <div className="text-xs space-y-1 max-h-[60vh] overflow-y-auto" data-testid="sub-audit">
      {!rows.length && <div className="text-muted-foreground">Kayıt yok.</div>}
      {rows.map((h) => <div key={h.id} className="border-b py-1">{fmtDT(h.at)} · <b>{AUDIT_TR[h.action] || h.action}</b> · {h.actor_email} ({h.actor_role})
        {h.meta?.from && ` · ${SUB_STATUS_TR[h.meta.from]} → ${SUB_STATUS_TR[h.meta.to]}`}{h.meta?.days ? ` · ${h.meta.days} gün` : ""}{h.meta?.reason ? ` · ${h.meta.reason}` : ""}{h.meta?.new_paid_until ? ` · Ücretli bitiş: ${fmtD(h.meta.old_paid_until)} → ${fmtD(h.meta.new_paid_until)}` : ""}{h.meta?.to && !h.meta?.from ? ` · ${h.meta.to}` : ""}</div>)}
    </div>
  );
};

const Settings = () => {
  const [d, setD] = useState("");
  useEffect(() => { api.get("/admin/subscriptions/settings").then(({ data }) => setD(String(data.trial_days))).catch(() => {}); }, []);
  const save = async () => { try { await api.put("/admin/subscriptions/settings", { trial_days: Number(d) }); toast.success("Varsayılan deneme süresi kaydedildi"); } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } };
  return (
    <div className="rounded-2xl border p-4 space-y-2 max-w-md" data-testid="sub-settings">
      <div className="font-semibold">Varsayılan ücretsiz deneme süresi</div>
      <div className="flex gap-2"><Input type="number" min={1} max={365} value={d} onChange={(e) => setD(e.target.value)} className="w-28" data-testid="sub-trial-days" /><span className="self-center text-sm">gün</span>
        <Button onClick={save} data-testid="sub-trial-days-save">Kaydet</Button></div>
      <p className="text-xs text-muted-foreground">Yalnızca bundan sonra onaylanan yeni işletmelere uygulanır; başlamış denemeler değişmez.</p>
    </div>
  );
};

export const AdminSubscriptions = () => {
  const [ov, setOv] = useState(null);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("providers");
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => { api.get("/admin/subscriptions/overview").then(({ data }) => setOv(data)).catch(() => toast.error("Abonelik verisi alınamadı")); }, []);
  useEffect(() => { load(); }, [load]);
  const open = async (id) => { try { const { data } = await api.get(`/admin/subscriptions/providers/${id}`); setDetail(data); } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } };
  const changed = () => { load(); if (detail) open(detail.provider.id); };
  if (!ov) return <div className="text-sm text-muted-foreground">Yükleniyor…</div>;
  const c = ov.counts;
  const pick = (f) => { setFilter(f); setTab("providers"); };
  return (
    <div className="space-y-4" data-testid="admin-subscriptions">
      <h2 className="text-lg font-bold">Abonelik Yönetimi</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2">
        <Kpi label="Toplam işletme" value={ov.total} testid="sub-kpi-total" onClick={() => pick("all")} active={tab === "providers" && filter === "all"} />
        <Kpi label="Ücretsiz deneme" value={c.trial} testid="sub-kpi-trial" onClick={() => pick("trial")} active={tab === "providers" && filter === "trial"} />
        <Kpi label="Aktif abonelik" value={c.active} testid="sub-kpi-active" onClick={() => pick("active")} active={tab === "providers" && filter === "active"} />
        <Kpi label="Süresi dolmuş" value={c.expired} testid="sub-kpi-expired" onClick={() => pick("expired")} active={tab === "providers" && filter === "expired"} />
        <Kpi label="7 gün içinde bitecek" value={ov.ending_soon} testid="sub-kpi-soon" onClick={() => pick("soon")} active={tab === "providers" && filter === "soon"} />
        <Kpi label="Geçiş bekliyor" value={c.migration_pending} testid="sub-kpi-migration" onClick={() => pick("migration_pending")} active={tab === "providers" && filter === "migration_pending"} />
        <Kpi label="Süresiz muaf" value={c.exempt} testid="sub-kpi-exempt" onClick={() => pick("exempt")} active={tab === "providers" && filter === "exempt"} />
        <Kpi label="Askıda" value={c.suspended} testid="sub-kpi-suspended" onClick={() => pick("suspended")} active={tab === "providers" && filter === "suspended"} />
        <Kpi label="Onay bekleyen işletme" value={c.pending_approval} testid="sub-kpi-pending-approval" onClick={() => pick("pending_approval")} active={tab === "providers" && filter === "pending_approval"} />
        <Kpi label="Onay bekleyen ödeme" value={ov.pending_payments} testid="sub-kpi-payments" onClick={() => setTab("payments")} active={tab === "payments"} />
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="providers" data-testid="sub-tab-providers">İşletmeler</TabsTrigger>
          <TabsTrigger value="payments" data-testid="sub-tab-payments">Ödemeler ({ov.pending_payments})</TabsTrigger>
          <TabsTrigger value="packages" data-testid="sub-tab-packages">Paketler</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="sub-tab-reviews">Deneme incelemesi ({ov.trial_reviews})</TabsTrigger>
          <TabsTrigger value="audit" data-testid="sub-tab-audit">İşlem geçmişi</TabsTrigger>
          <TabsTrigger value="settings" data-testid="sub-tab-settings">Ayarlar</TabsTrigger>
        </TabsList>
        <TabsContent value="providers"><Providers ov={ov} filter={filter} onOpen={open} /></TabsContent>
        <TabsContent value="payments"><SubPayments onChanged={load} /></TabsContent>
        <TabsContent value="packages"><SubPackages /></TabsContent>
        <TabsContent value="reviews"><TrialReviews onOpen={open} onChanged={load} /></TabsContent>
        <TabsContent value="audit"><AuditLog /></TabsContent>
        <TabsContent value="settings"><Settings /></TabsContent>
      </Tabs>
      <ProviderSubDialog detail={detail} onClose={() => setDetail(null)} onChanged={changed} />
    </div>
  );
};
